import React, { useEffect, useState } from "react";
import { authApi, teamApi } from "../../lib/api";
import { toast } from "sonner";
import { X, Mail, Check, Trash2, KeyRound } from "lucide-react";
import { Avatar } from "./TeamChips";
import { AccessPicker } from "./AccessPicker";

const LABEL = { deal: "deal", raise: "fundraising" };

export const AssignTeamModal = ({ open, onClose, scope, dealKey, dealName, rows, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const [tabs, setTabs] = useState([]);
  const [pickNew, setPickNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => { if (open) { authApi.users().then(setUsers).catch(() => {}); setEmail(""); setName(""); setEditing(null); } }, [open]);
  if (!open) return null;

  const byUser = Object.fromEntries(rows.map((a) => [a.userId, a]));
  const run = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); onChanged(); if (ok) toast.success(typeof ok === "function" ? ok(r) : ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  const toggle = (u) => byUser[u.id]
    ? run(u.id, () => teamApi.unassign(byUser[u.id].id), `${u.name || u.email} removed`)
    : run(u.id, () => teamApi.assign({ scope, dealKey, dealName, userIds: [u.id], role, tabs, notify: true }), `${u.name || u.email} added — notified by email`);
  const invite = (e) => {
    e.preventDefault();
    run("invite", () => teamApi.invite({ email: email.trim(), name: name.trim(), scope, dealKey, dealName, role, tabs }), (r) => {
      setEmail(""); setName("");
      if (r.created && r.delivered) return `Invited ${r.user.email} — login emailed`;
      if (r.created) return `Account created for ${r.user.email}. Temp password: ${r.tempPassword} (email not delivered — share it manually)`;
      return `${r.user.email} already had an account — assigned`;
    });
  };
  const accessLabel = (t) => (!t || !t.length ? "Entire deal" : `${t.length} tab${t.length === 1 ? "" : "s"}`);

  return (
    <div className="modal-overlay" data-testid="assign-team-modal" onMouseDown={onClose}>
      <div className="modal-card" style={{ maxWidth: 560, width: "94%" }} onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>Assign the {LABEL[scope] || scope} team</div>
          <button className="btn btn-sm" onClick={onClose} data-testid="assign-team-close"><X size={14} /></button>
        </div>
        <div className="modal-sub">{dealName || "This deal"} — who is working on the {LABEL[scope] || scope}. Everyone you add gets an email.</div>

        <div className="tm-newrow">
          <div className="field" style={{ marginBottom: 0 }}><label>Role (optional)</label>
            <input className="inp" placeholder="Lead, Analyst, Closer…" value={role} onChange={(e) => setRole(e.target.value)} data-testid="assign-role" /></div>
          <div className="field" style={{ marginBottom: 0 }}><label>Access for new adds</label>
            <button className="btn tm-access-btn" onClick={() => setPickNew(!pickNew)} data-testid="assign-access-toggle"><KeyRound size={12} />{accessLabel(tabs)}</button></div>
        </div>
        {pickNew && <AccessPicker value={tabs} onChange={setTabs} onDone={() => setPickNew(false)} />}

        <div className="section-label">Team members ({users.length})</div>
        <div className="tm-list">
          {users.filter((u) => u.role !== "admin" || byUser[u.id]).map((u) => {
            const a = byUser[u.id];
            return (
              <div key={u.id} className={`tm-user ${a ? "on" : ""}`} data-testid={`assign-user-${u.id}`}>
                <Avatar name={u.name} email={u.email} size={26} />
                <span className="tm-user-txt"><b>{u.name || u.email}</b><small>{u.email}{a?.role ? ` · ${a.role}` : ""}{a ? ` · ${accessLabel(a.tabs)}` : ""}</small></span>
                <span className="tm-user-acts">
                  {a && <button className="btn btn-sm" onClick={() => setEditing(editing === a.id ? null : a.id)} title="Change what they can see" data-testid={`assign-access-${u.id}`}><KeyRound size={11} /></button>}
                  <button className={`btn btn-sm ${a ? "" : "btn-primary"}`} onClick={() => toggle(u)} disabled={busy === u.id} data-testid={`assign-toggle-${u.id}`}>
                    {a ? <><Check size={12} />Assigned <Trash2 size={11} /></> : "Add"}
                  </button>
                </span>
                {a && editing === a.id && (
                  <div className="tm-user-access">
                    <AccessPicker value={a.tabs || []} onChange={(t) => run(`acc-${a.id}`, () => teamApi.patchAssignment(a.id, { tabs: t }), `${u.name || u.email} now sees ${!t.length ? "the entire deal" : `${t.length} tab(s)`}`)} onDone={() => setEditing(null)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="section-label">Invite someone new by email</div>
        <form onSubmit={invite} className="tm-invite">
          <input className="inp" type="email" placeholder="teammate@xyzvtr.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="invite-email" />
          <input className="inp" placeholder="Name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
          <button className="btn btn-primary" type="submit" disabled={busy === "invite"} data-testid="invite-submit"><Mail size={12} />{busy === "invite" ? "Inviting…" : "Invite & assign"}</button>
        </form>
        <div className="cm-empty">Creates their login, assigns them here with the access above, and emails a temporary password.</div>
      </div>
    </div>
  );
};
